import { useEffect, useState } from "react";
import {
  featureFlagEnabled,
  subscribeToFeatureFlags,
} from "@/lib/analytics/posthog";
import type {
  EstateFleetRow,
  EstateRepositoryRow,
  PageMeta,
} from "@/lib/client/estate-inventory";
import { demoEstatePreviewEnabled } from "@/lib/preview-flags";

export const DEMO_ESTATE_DATA_FLAG = "client-demo-estate-data";
const STORAGE_KEY = "autar.demo-estate.overlay.v1";
const CHANGE_EVENT = "autar:demo-estate-change";

type DemoOverlay = {
  assignments: Record<string, string>;
  fleets: Array<{ id: string; name: string }>;
  fleetNames: Record<string, string>;
  archivedFleetIds: string[];
  deletedFleetIds: string[];
};

const BASE_FLEETS = [
  { id: "frontend-fleet", name: "Frontend" },
  { id: "backend-fleet", name: "Backend" },
  { id: "data-fleet", name: "Data" },
] as const;

const BASE_REPOSITORIES = [
  [
    "storefront",
    "acme-online-retail/storefront",
    "frontend-fleet",
    "TypeScript",
  ],
  [
    "account-portal",
    "acme-online-retail/account-portal",
    "frontend-fleet",
    "TypeScript",
  ],
  [
    "design-system",
    "acme-online-retail/design-system",
    "frontend-fleet",
    "TypeScript",
  ],
  [
    "mobile-app",
    "acme-online-retail/mobile-app",
    "frontend-fleet",
    "TypeScript",
  ],
  ["platform", "acme-online-retail/platform", "backend-fleet", "PHP"],
  [
    "payments-gateway",
    "acme-online-retail/payments-gateway",
    "backend-fleet",
    "Go",
  ],
  [
    "notifications",
    "acme-online-retail/notifications",
    "backend-fleet",
    "TypeScript",
  ],
  [
    "search-indexer",
    "acme-online-retail/search-indexer",
    "backend-fleet",
    "Java",
  ],
  ["data-pipeline", "acme-online-retail/data-pipeline", "data-fleet", "Python"],
  [
    "analytics-warehouse",
    "acme-online-retail/analytics-warehouse",
    "data-fleet",
    "SQL",
  ],
  [
    "infra-terraform",
    "acme-online-retail/infra-terraform",
    "unassigned",
    "HCL",
  ],
  [
    "marketing-site",
    "acme-online-retail/marketing-site",
    "unassigned",
    "TypeScript",
  ],
  ["legacy-reports", "acme-online-retail/legacy-reports", "unassigned", "PHP"],
  ["oss-php", "php/php-src", "dependencies", "C"],
  ["oss-laravel-framework", "laravel/framework", "dependencies", "PHP"],
  ["oss-symfony-framework-bundle", "symfony/symfony", "dependencies", "PHP"],
  ["oss-frankenphp", "dunglas/frankenphp", "dependencies", "Go"],
  ["oss-litellm", "BerriAI/litellm", "dependencies", "Python"],
] as const;

const emptyOverlay = (): DemoOverlay => ({
  assignments: {},
  fleets: [],
  fleetNames: {},
  archivedFleetIds: [],
  deletedFleetIds: [],
});

function readOverlay(): DemoOverlay {
  if (typeof sessionStorage === "undefined") return emptyOverlay();
  try {
    const value = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? "null");
    return value && typeof value === "object"
      ? {
          assignments:
            value.assignments && typeof value.assignments === "object"
              ? value.assignments
              : {},
          fleets: Array.isArray(value.fleets) ? value.fleets : [],
          fleetNames:
            value.fleetNames && typeof value.fleetNames === "object"
              ? value.fleetNames
              : {},
          archivedFleetIds: Array.isArray(value.archivedFleetIds)
            ? value.archivedFleetIds
            : [],
          deletedFleetIds: Array.isArray(value.deletedFleetIds)
            ? value.deletedFleetIds
            : [],
        }
      : emptyOverlay();
  } catch {
    return emptyOverlay();
  }
}

function writeOverlay(value: DemoOverlay) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function demoEstateDataEnabled() {
  return (
    demoEstatePreviewEnabled || featureFlagEnabled(DEMO_ESTATE_DATA_FLAG, false)
  );
}

export function useDemoEstateDataMode() {
  const [enabled, setEnabled] = useState(demoEstateDataEnabled);
  useEffect(() => {
    const update = () => setEnabled(demoEstateDataEnabled());
    const unsubscribe = subscribeToFeatureFlags(update);
    window.addEventListener(CHANGE_EVENT, update);
    return () => {
      unsubscribe();
      window.removeEventListener(CHANGE_EVENT, update);
    };
  }, []);
  return enabled;
}

export function clearDemoEstateSession() {
  if (typeof sessionStorage !== "undefined")
    sessionStorage.removeItem(STORAGE_KEY);
}

// The Fleets this data makes up exist only in the browser. The API refuses
// their ids as malformed, so a request must never name one.
export function isDemoFleetId(id: string) {
  return (
    id === "unassigned" ||
    id === "dependencies" ||
    [...BASE_FLEETS, ...readOverlay().fleets].some((fleet) => fleet.id === id)
  );
}

const slug = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

export function demoEstateInventory() {
  const overlay = readOverlay();
  const fleetSeeds = [...BASE_FLEETS, ...overlay.fleets]
    .filter((fleet) => !overlay.deletedFleetIds.includes(fleet.id))
    .map((fleet) => ({
      ...fleet,
      name: overlay.fleetNames[fleet.id] ?? fleet.name,
    }));
  const fleetNames = new Map(fleetSeeds.map((fleet) => [fleet.id, fleet.name]));
  fleetNames.set("unassigned", "Unassigned");
  fleetNames.set("dependencies", "Supply Chain Dependencies");

  const repositories: EstateRepositoryRow[] = BASE_REPOSITORIES.map(
    ([id, fullName, originalFleetId, language]) => {
      const fleetId = overlay.assignments[id] ?? originalFleetId;
      const fleetName = fleetNames.get(fleetId) ?? "Unassigned";
      const normalizedFleetId = fleetNames.has(fleetId)
        ? fleetId
        : "unassigned";
      return {
        id,
        name: fullName.split("/").at(-1) ?? fullName,
        fullName,
        platform: "github",
        private: fullName.startsWith("acme-online-retail/"),
        language,
        createdAt: "2026-10-28T15:00:00.000Z",
        fleetId: normalizedFleetId,
        fleet: {
          id: normalizedFleetId,
          name: fleetName,
          slug: slug(fleetName),
          kind: normalizedFleetId === "unassigned" ? "system" : "user",
          archivedAt: null,
        },
      };
    },
  );

  const fleets: EstateFleetRow[] = [
    ...fleetSeeds.map((fleet) => ({
      id: fleet.id,
      name: fleet.name,
      slug: slug(fleet.name),
      kind: "user" as const,
      createdAt: "2026-10-28T15:00:00.000Z",
      archivedAt: overlay.archivedFleetIds.includes(fleet.id)
        ? "2026-10-28T15:00:00.000Z"
        : null,
      isDefault: fleet.id === "frontend-fleet",
      isSystem: false,
      repositoryCount: repositories.filter((repo) => repo.fleetId === fleet.id)
        .length,
      criticalWarnings: fleet.id === "backend-fleet" ? 5 : 0,
      access: { directUsers: 0, groups: 0 },
    })),
    {
      id: "unassigned",
      name: "Unassigned",
      slug: "unassigned",
      kind: "system" as const,
      createdAt: "2026-10-28T15:00:00.000Z",
      archivedAt: null,
      isDefault: false,
      isSystem: true,
      repositoryCount: repositories.filter(
        (repo) => repo.fleetId === "unassigned",
      ).length,
      criticalWarnings: null,
      access: { directUsers: 0, groups: 0 },
    },
  ];
  return { fleets, repositories };
}

function page<T>(data: T[], currentPage: number, perPage: number) {
  const lastPage = Math.max(1, Math.ceil(data.length / perPage));
  const safePage = Math.min(Math.max(1, currentPage), lastPage);
  const meta: PageMeta = {
    total: data.length,
    lastPage,
    currentPage: safePage,
    perPage,
    prev: safePage > 1 ? safePage - 1 : null,
    next: safePage < lastPage ? safePage + 1 : null,
  };
  return {
    data: data.slice((safePage - 1) * perPage, safePage * perPage),
    meta,
  };
}

export function listDemoFleets(options: {
  page: number;
  perPage: number;
  search: string;
  status: string;
  kind?: "user";
}) {
  const query = options.search.trim().toLowerCase();
  const rows = demoEstateInventory().fleets.filter((fleet) => {
    const matchesStatus =
      options.status === "all" ||
      (options.status === "active" && !fleet.archivedAt) ||
      (options.status === "archived" && Boolean(fleet.archivedAt));
    return (
      matchesStatus &&
      (!options.kind || fleet.kind === options.kind) &&
      (!query || fleet.name.toLowerCase().includes(query))
    );
  });
  return Promise.resolve(page(rows, options.page, options.perPage));
}

export function listDemoRepositories(options: {
  page: number;
  perPage: number;
  search: string;
  fleet: string;
}) {
  const query = options.search.trim().toLowerCase();
  const rows = demoEstateInventory().repositories.filter(
    (repo) =>
      (options.fleet === "all" || repo.fleetId === options.fleet) &&
      (!query || repo.fullName.toLowerCase().includes(query)),
  );
  return Promise.resolve(page(rows, options.page, options.perPage));
}

export function moveDemoRepositories(
  repositoryIds: string[],
  destination: string,
) {
  const overlay = readOverlay();
  for (const id of repositoryIds) overlay.assignments[id] = destination;
  writeOverlay(overlay);
}

export function createDemoFleet(name: string) {
  const overlay = readOverlay();
  const id = `demo-${slug(name)}-${Date.now().toString(36)}`;
  overlay.fleets.push({ id, name });
  writeOverlay(overlay);
  return id;
}

export function renameDemoFleet(id: string, name: string) {
  const overlay = readOverlay();
  overlay.fleetNames[id] = name;
  writeOverlay(overlay);
  return true;
}

export function archiveDemoFleet(id: string, archived: boolean) {
  const overlay = readOverlay();
  overlay.archivedFleetIds = archived
    ? [...new Set([...overlay.archivedFleetIds, id])]
    : overlay.archivedFleetIds.filter((fleetId) => fleetId !== id);
  writeOverlay(overlay);
}

export function deleteDemoFleet(id: string) {
  const overlay = readOverlay();
  overlay.deletedFleetIds = [...new Set([...overlay.deletedFleetIds, id])];
  for (const [repositoryId, fleetId] of Object.entries(overlay.assignments))
    if (fleetId === id) overlay.assignments[repositoryId] = "unassigned";
  writeOverlay(overlay);
}
